import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shuffle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ADVENT_PRIZE_LIST } from "@/lib/advent";

type Door = { day: number; kind: string; title: string; body: string; prize: string | null };
type Entry = { id: string; first_name: string; email: string; won_prize: string | null; created_at: string; street: string | null; postal_code: string | null; city: string | null; country: string | null };

const AdminAdvent = () => {
  const [doors, setDoors] = useState<Door[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [tickets, setTickets] = useState<{ entry_id: string; day: number }[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [views, setViews] = useState({ total: 0, week: 0, today: 0 });

  const load = async () => {
    const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
    const week = new Date(Date.now() - 7 * 864e5).toISOString();
    const count = (since?: string) => {
      let q = supabase.from("advent_page_views").select("id", { count: "exact", head: true });
      if (since) q = q.gte("created_at", since);
      return q;
    };
    const [d, e, t, vt, vw, vd] = await Promise.all([
      supabase.from("advent_doors").select("day,kind,title,body,prize").order("day"),
      supabase.from("advent_entries").select("*").order("created_at"),
      supabase.from("advent_tickets").select("entry_id,day").eq("season", Number(new Intl.DateTimeFormat("en", { timeZone: "Europe/Vienna", year: "numeric" }).format(new Date()))),
      count(), count(week), count(midnight.toISOString()),
    ]);
    setDoors(d.data ?? []);
    setEntries(e.data ?? []);
    setTickets(t.data ?? []);
    setViews({ total: vt.count ?? 0, week: vw.count ?? 0, today: vd.count ?? 0 });
  };
  useEffect(() => { load(); }, []);

  const save = async (d: Door) => {
    const { error } = await supabase.from("advent_doors").update({ title: d.title, body: d.body, prize: d.prize || null, updated_at: new Date().toISOString() }).eq("day", d.day);
    error ? toast.error("Speichern fehlgeschlagen") : toast.success(`Türchen ${d.day} gespeichert`);
  };

  const draw = async () => {
    if (entries.some((e) => e.won_prize)) return toast.error("Es wurde bereits gezogen.");
    if (!confirm(`Jetzt bis zu ${ADVENT_PRIZE_LIST.length} Gewinner ziehen? Jede Teilnahme zählt als Los, jede Person kann nur einmal gewinnen.`)) return;
    setDrawing(true);
    try {
      const { data, error } = await supabase.rpc("draw_advent_winners");
      if (error) return toast.error(error.message.includes("draw_not_ready") ? "Die Verlosung ist erst ab 24. Dezember, 12:00 Uhr möglich." : "Die Verlosung konnte nicht durchgeführt werden.");
      toast.success(`${data} Gewinner gezogen`);
      await load();
    } finally { setDrawing(false); }
  };

  const upd = (day: number, patch: Partial<Door>) => setDoors((ds) => ds.map((d) => (d.day === day ? { ...d, ...patch } : d)));
  const activeEntries = entries.filter((e) => tickets.some((t) => t.entry_id === e.id));
  const winners = activeEntries.filter((e) => e.won_prize);

  return (
    <div className="min-h-screen bg-secondary">
      <div className="container mx-auto px-4 py-10 max-w-4xl space-y-8">
        <div>
          <Button variant="ghost" size="sm" asChild><Link to="/admin"><ArrowLeft className="w-4 h-4 mr-1" />Dashboard</Link></Button>
          <h1 className="text-3xl font-bold text-foreground mt-2">Adventkalender</h1>
          <p className="text-muted-foreground">Öffentlich unter <a className="text-primary underline" href="/adventkalender" target="_blank" rel="noreferrer">/adventkalender</a></p>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-3">Aufrufe der Adventkalender-Seite</h2>
          <div className="grid grid-cols-3 gap-3 text-center">
            {([["Gesamt", views.total], ["Letzte 7 Tage", views.week], ["Heute", views.today]] as const).map(([l, v]) => (
              <div key={l} className="rounded-xl bg-secondary p-3"><p className="text-2xl font-bold text-foreground">{v.toLocaleString("de-AT")}</p><p className="text-xs text-muted-foreground">{l}</p></div>
            ))}
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">Lostopf: {tickets.length} Lose · {activeEntries.length} Personen</h2>
            <Button onClick={draw} disabled={drawing || !tickets.length || winners.length > 0}><Shuffle className="w-4 h-4 mr-1.5" />{drawing ? "Ziehung läuft…" : "Gewinner ziehen"}</Button>
          </div>
          {winners.length > 0 && (
            <ul className="text-sm space-y-1">
              {winners.map((w) => <li key={w.id}><strong>{w.won_prize}</strong> – {w.first_name} ({w.email})<br />{w.street}, {w.postal_code} {w.city}, {w.country}</li>)}
            </ul>
          )}
          <details className="text-sm"><summary className="cursor-pointer text-muted-foreground">Alle Teilnahmen anzeigen</summary>
            <ul className="mt-2 space-y-3">{entries.map((e) => <li key={e.id}>{e.first_name} – {e.email}<br />{e.street}, {e.postal_code} {e.city}, {e.country}<br /><span className="text-muted-foreground">{tickets.filter((t) => t.entry_id === e.id).length} Lose · Türchen {tickets.filter((t) => t.entry_id === e.id).map((t) => t.day).sort((a,b) => a-b).join(", ") || "noch keine Türchen-Teilnahme"}</span></li>)}</ul>
          </details>
        </Card>

        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Türchen bearbeiten</h2>
          {doors.map((d) => (
            <Card key={d.day} className="p-5 space-y-3">
              <p className="text-sm text-primary font-medium">Türchen {d.day} · {d.kind}</p>
              <Input value={d.title} onChange={(e) => upd(d.day, { title: e.target.value })} />
              <Textarea rows={4} value={d.body} onChange={(e) => upd(d.day, { body: e.target.value })} />
              {d.kind === "geschenk" && <Input placeholder="Geschenk" value={d.prize ?? ""} onChange={(e) => upd(d.day, { prize: e.target.value })} />}
              <Button size="sm" variant="outline" onClick={() => save(d)}>Speichern</Button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAdvent;
